import { Directive, ElementRef, Renderer2 } from '@angular/core';

export type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';

export type TooltipFullTextOrientation = 'vertical' | 'horizontal';

// written in preferred order according to requirements
const ALL_TOOLTIP_POSITIONS: TooltipPosition[] = ['bottom', 'top', 'right', 'left'];

export const TOOLTIP_BASE_CLASS = 'info-tooltip';

enum KeyboardKeys {
	Escape = 'Escape',
	Enter = 'Enter',
	Space = 'Space',
}

interface TooltipRect {
	bottom: number;
	height: number;
	left: number;
	right: number;
	top: number;
	width: number;
	x: number;
	y: number;
}

@Directive()
export abstract class TooltipBaseDirective {
	public abstract tooltipTitle: string;
	public abstract placement: TooltipPosition;
	public abstract elementRef: ElementRef;
	public abstract renderer: Renderer2;
	public abstract document: Document;

	public tooltip: HTMLElement | null = null;
	public readonly OFFSET = 10;
	public readonly MAX_WIDTH = 340;
	public readonly LAYOUT_OFFSET = 10;
	public readonly boundMousedownHandler: () => unknown = this.onClick.bind(this);
	public readonly boundKeydownHandler: (event: KeyboardEvent) => unknown = this.handleKeyDown.bind(this);

	protected constructor() {}

	public show(): void {
		const globalLayout = this.document.querySelector('app-root') as HTMLElement;

		this.create(globalLayout);
		this.renderTooltip(this.tooltip, { top: 0, left: 0 }); // avoid changing global layout sizes

		const tooltipPos = this.setPosition(globalLayout);

		this.renderer.addClass(this.tooltip, 'info-tooltip-show');
		if (tooltipPos.width < this.MAX_WIDTH) {
			this.renderer.addClass(this.tooltip, 'single-line');
			this.renderer.setStyle(this.tooltip, 'width', `${tooltipPos.width}px`);
		}
	}

	public hide(): void {
		if (!this.tooltip) {
			return;
		}
		this.renderer.removeClass(this.tooltip, 'info-tooltip-show');
		this.renderer.removeChild(this.elementRef.nativeElement, this.tooltip);
		this.tooltip = null;
	}

	public create(globalLayout: HTMLElement): void {
		const element = this.elementRef.nativeElement;

		this.tooltip = this.renderer.createElement('span');

		const nestedSpan = this.renderer.createElement('span');
		const tooltipText = this.renderer.createText(this.tooltipTitle || element.textContent);

		this.renderer.addClass(this.tooltip, TOOLTIP_BASE_CLASS);

		// Add elements to the DOM
		this.renderer.appendChild(nestedSpan, tooltipText);
		this.renderer.appendChild(this.tooltip, nestedSpan);
		this.renderer.appendChild(globalLayout, this.tooltip);

		if (this.tooltip) {
			const tooltipComputedStyles = getComputedStyle(this.tooltip);
			const maxHeight = this.getNumberFromStr(tooltipComputedStyles.getPropertyValue('max-height'));

			const isMaxHeightReached = this.tooltip.getBoundingClientRect().height >= maxHeight;

			if (isMaxHeightReached) {
				const lineHeight = this.getNumberFromStr(tooltipComputedStyles.getPropertyValue('line-height'));
				const countOfVisibleTextRows = Math.floor(maxHeight / lineHeight);
				this.renderer.addClass(this.tooltip, 'max-height-reached');
				this.renderer.setStyle(this.tooltip, '-webkit-line-clamp', countOfVisibleTextRows);
			}
		}
	}

	// e.g. get number from '123px'
	private getNumberFromStr(str: string): number {
		return Number(str.replace(/[^0-9]/g, ''));
	}

	// tslint:disable-next-line:cyclomatic-complexity
	public setPosition(globalLayout: HTMLElement): TooltipRect {
		const hostPos = this.elementRef.nativeElement.getBoundingClientRect();
		const tooltipPos = (this.tooltip as HTMLElement).getBoundingClientRect();
		const scrollPos =
			window.pageYOffset || this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;
		const globalLayoutRect = globalLayout?.getBoundingClientRect();
		const leftLayoutPosition = globalLayoutRect.left as number;

		let xDelta = 0;
		if (leftLayoutPosition < 0) {
			xDelta = Math.abs(leftLayoutPosition);
		}

		const { position, placement: finalPlacement } = this.getAvailablePosition(
			this.placement,
			ALL_TOOLTIP_POSITIONS,
			tooltipPos,
			hostPos,
			globalLayoutRect,
			xDelta,
		);

		// slightly move to the right
		if (typeof leftLayoutPosition === 'number' && position.left <= leftLayoutPosition) {
			position.left = leftLayoutPosition + this.LAYOUT_OFFSET;
		}

		this.renderer.addClass(this.tooltip, `info-tooltip-${finalPlacement}`);

		position.top = position.top + scrollPos;

		if (finalPlacement === 'left' || finalPlacement === 'right') {
			// align triangle in the middle of host
			this.setTriangleOffsetTop(this.tooltip, hostPos, position, scrollPos);
		}

		this.renderTooltip(this.tooltip, position);

		return tooltipPos;
	}

	private setTriangleOffsetTop(
		tooltip: HTMLElement | null,
		hostRect: DOMRect,
		position: { top: number; left: number },
		scrollPos: number,
	): void {
		const hostMiddlePoint = hostRect.top + hostRect.height / 2;
		const offset = hostMiddlePoint - position.top + scrollPos;

		this.renderer.addClass(tooltip, 'triangle-offset-top');
		this.renderer.setStyle(tooltip, '--triangle-offset-top', `${offset}px`, 2);
	}

	private getAvailablePosition(
		placement: TooltipPosition,
		placementsToCheck: TooltipPosition[],
		tooltipRect: DOMRect,
		hostRect: DOMRect,
		layoutRect: DOMRect,
		xDelta: number,
	): { placement: TooltipPosition; position: { top: number; left: number } } {
		if (!placementsToCheck.length) {
			console.warn('Viewport is too small to contain tooltip');
			const calcPosition = this.calcPosition(placement, hostRect, tooltipRect, layoutRect, xDelta);

			return { placement, position: calcPosition };
		}

		const position = this.calcPosition(placement, hostRect, tooltipRect, layoutRect, xDelta);
		const placementIndex = placementsToCheck.findIndex((placementToCheck) => placementToCheck === placement);
		const isPlacementAvailable = this.checkIfHasEnoughSpaceWithPosition(placement, position, tooltipRect, layoutRect);

		if (isPlacementAvailable) {
			return { placement, position };
		}

		const freePlacementsToCheck = [...placementsToCheck];
		freePlacementsToCheck.splice(placementIndex, 1);

		return this.getAvailablePosition(
			freePlacementsToCheck[0],
			freePlacementsToCheck,
			tooltipRect,
			hostRect,
			layoutRect,
			xDelta,
		);
	}

	private checkIfHasEnoughSpaceWithPosition(
		placement: TooltipPosition,
		position: { top: number; left: number },
		tooltipRect: DOMRect,
		layoutRect: DOMRect,
	): boolean {
		switch (placement) {
			case 'bottom':
				return (
					layoutRect.height - position.top >= tooltipRect.height &&
					position.left >= layoutRect.left &&
					position.left + tooltipRect.width <= layoutRect.width
				);
			case 'top':
				return (
					position.top >= layoutRect.top &&
					position.top + tooltipRect.height <= layoutRect.height &&
					position.top + tooltipRect.height <= layoutRect.height &&
					position.left >= layoutRect.left &&
					position.left + tooltipRect.width <= layoutRect.width
				);
			case 'right':
				return layoutRect.width + layoutRect.left - position.left >= tooltipRect.width;
			case 'left':
				return (
					position.left >= layoutRect.left && position.left + tooltipRect.width <= layoutRect.width + layoutRect.left
				);
		}
	}

	public calcPosition(
		placement: string,
		hostPos: TooltipRect,
		tooltipPos: { width: number; height: number },
		layoutRect: DOMRect,
		xDelta = 0,
	): { top: number; left: number } {
		const position = { top: 0, left: 0 };

		switch (placement) {
			case 'bottom':
				position.top = hostPos.bottom + this.OFFSET;
				position.left = xDelta + hostPos.left + (hostPos.width - tooltipPos.width) / 2;
				break;
			case 'left':
				position.top = this.calcTopPositionForLeftRightTooltip(hostPos, tooltipPos, layoutRect);
				position.left = xDelta + hostPos.left - tooltipPos.width - this.OFFSET;
				break;
			case 'right':
				position.top = this.calcTopPositionForLeftRightTooltip(hostPos, tooltipPos, layoutRect);
				position.left = xDelta + hostPos.right + this.OFFSET;
				break;
			default:
				position.top = hostPos.top - tooltipPos.height - this.OFFSET;
				position.left = xDelta + hostPos.left + (hostPos.width - tooltipPos.width) / 2;
				break;
		}

		return position;
	}

	private calcTopPositionForLeftRightTooltip(
		hostPos: TooltipRect,
		tooltipPos: { width: number; height: number },
		layoutRect: DOMRect,
	): number {
		const positionTop = hostPos.top + (hostPos.height - tooltipPos.height) / 2;

		const isPositionTopOverlaysLayoutFromTop = positionTop < layoutRect.top;
		const isPositionTopOverlaysLayoutFromBottom = positionTop + tooltipPos.height > layoutRect.height;

		if (isPositionTopOverlaysLayoutFromTop) {
			return this.OFFSET;
		}

		if (isPositionTopOverlaysLayoutFromBottom) {
			return positionTop - (positionTop + tooltipPos.height - layoutRect.height) - this.OFFSET;
		}

		return positionTop;
	}

	public renderTooltip(element: HTMLElement | null, position: { top: number; left: number }): void {
		this.renderer.setStyle(element, 'top', `${position.top}px`);
		this.renderer.setStyle(element, 'left', `${position.left}px`);
	}

	public onClick(): void {
		if (this.tooltip) {
			this.hide();
			this.removeHandlers();
		}
	}

	public handleKeyDown(event: KeyboardEvent): void {
		if (event.key in KeyboardKeys && this.tooltip) {
			this.hide();
			this.removeHandlers();
		}
	}

	public attachHandlers(): void {
		this.document.addEventListener('mousedown', this.boundMousedownHandler);
		this.document.addEventListener('keydown', this.boundKeydownHandler);
	}

	public removeHandlers(): void {
		this.document.removeEventListener('mousedown', this.boundMousedownHandler);
		this.document.removeEventListener('keydown', this.boundKeydownHandler);
	}
}
